import { useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

/* Shared pieces of the admin console. Deliberately plain: this is a working
   surface, not a marketing page, so it favours legibility and obvious state
   over atmosphere. */

export const inputClass =
  'w-full rounded border border-hairline bg-ink-deep/60 px-3 py-2 text-sm text-bright placeholder-faint transition-colors focus:border-seal focus:outline-none';

export function Field({ label, help, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-bright">
        {label}
      </label>
      {children}
      {help && <p className="mt-1 text-xs leading-snug text-faint">{help}</p>}
    </div>
  );
}

export function Pill({ tone = 'neutral', children }) {
  const tones = {
    neutral: 'border-hairline text-slate',
    good: 'border-seal/60 text-seal',
    warn: 'border-amber-400/60 text-amber-300',
    bad: 'border-rose-400/60 text-rose-300',
  };
  return (
    <span className={`inline-block rounded border px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Toast({ message, tone = 'good' }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded border px-4 py-2 text-sm shadow-lg ${
        tone === 'bad' ? 'border-rose-400/60 bg-ink text-rose-200' : 'border-seal/60 bg-ink text-seal'
      }`}
    >
      {message}
    </div>
  );
}

/* Uploads a file to Firebase Storage under the given folder and reports
   progress. Returns the public download URL through onDone. */
export function UploadButton({ folder, accept, label = 'Upload file', onDone, onError, maxMb = 200 }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  function pick() {
    if (inputRef.current) inputRef.current.click();
  }

  function handleChange(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      onError?.(`That file is larger than ${maxMb} MB.`);
      return;
    }
    if (!storage) {
      onError?.('File storage is unavailable: the Firebase configuration is incomplete.');
      return;
    }
    const safeName = file.name.replace(/[^\w.-]+/g, '-').toLowerCase();
    const path = `${folder}/${Date.now()}-${safeName}`;
    const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type });
    setProgress(0);
    task.on(
      'state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (error) => {
        setProgress(null);
        onError?.(
          error.code === 'storage/unauthorized'
            ? 'Storage rejected the upload. Confirm your account is in the admins collection and the storage rules are deployed.'
            : 'Upload failed. Please try again.'
        );
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setProgress(null);
        onDone?.(url, file);
      }
    );
  }

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} onChange={handleChange} className="hidden" />
      <button
        type="button"
        onClick={pick}
        disabled={progress !== null}
        className="rounded border border-hairline px-3 py-2 text-sm font-semibold text-bright transition-colors hover:border-seal hover:text-seal disabled:opacity-50"
      >
        {progress === null ? label : `Uploading ${progress}%`}
      </button>
      {progress !== null && (
        <div className="mt-2 h-1 w-full rounded-full bg-ink-deep">
          <div className="h-1 rounded-full bg-seal transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
